import type { UploadTarget } from "@/types/uploadTarget";

export interface UnpersistedUploadFile {
	index: number;
	name: string;
	size: number;
	type: string;
	target?: UploadTarget;
	sessionId?: string;
	persisted?: boolean;
	failureMessage?: string;
}
export interface UnpersistedUploadNotice {
	conversationId: string;
	organizationId: string;
	uploadBatchId: string;
	laneId: string;
	createdAt: number;
	files: UnpersistedUploadFile[];
}
const PREFIX = "admin.unpersistedUploadNotices.v1:";
const TTL = 7 * 24 * 60 * 60 * 1000;
const MAX_BATCHES = 20;

// Store only abort identities: presigned URLs and source bytes do not belong in localStorage.
function abortIdentity(target: UploadTarget): UploadTarget {
	if (target.type === "direct")
		return {
			type: "direct",
			presignedUrl: "",
			family: target.family,
			registryId: target.registryId,
			attemptId: target.attemptId,
			generation: target.generation,
			takeGeneration: target.takeGeneration
		};
	if (target.family === "uploads")
		return {
			type: "multipart",
			family: "uploads",
			registryId: target.registryId,
			attemptId: target.attemptId,
			generation: target.generation,
			takeGeneration: target.takeGeneration,
			envelopes: []
		};
	return { type: "multipart", family: "legacy", uploadId: target.uploadId, s3Key: target.s3Key, bucket: target.bucket, chunkUrls: [] };
}
function validTarget(value: unknown): boolean {
	if (value === undefined) return true;
	if (!value || typeof value !== "object") return false;
	const target = value as Record<string, unknown>;
	if (target.type !== "direct" && target.type !== "multipart") return false;
	if (target.family === "uploads")
		return (
			typeof target.registryId === "string" &&
			target.registryId.length > 0 &&
			typeof target.attemptId === "string" &&
			target.attemptId.length > 0 &&
			typeof target.generation === "number" &&
			Number.isSafeInteger(target.generation) &&
			target.generation > 0 &&
			(target.takeGeneration === undefined || typeof target.takeGeneration === "string")
		);
	return (
		target.type === "direct" ||
		[target.uploadId, target.s3Key, target.bucket].every((value) => typeof value === "string" && value.length > 0)
	);
}
function write(notice: UnpersistedUploadNotice): boolean {
	try {
		const serialized = JSON.stringify(notice);
		// ponytail: 1 MB per batch and 20 batches; revisit only if supported selections outgrow metadata storage.
		if (serialized.length > 1_000_000) throw new Error("Upload notice metadata exceeds 1 MB");
		if (notice.files.length) localStorage.setItem(PREFIX + notice.uploadBatchId, serialized);
		else localStorage.removeItem(PREFIX + notice.uploadBatchId);
		return true;
	} catch (error) {
		console.warn("[Upload] Could not update unpersisted batch notice", {
			conversationId: notice.conversationId,
			uploadBatchId: notice.uploadBatchId,
			error
		});
		return false;
	}
}
export function readUploadBatchRecords(): UnpersistedUploadNotice[] {
	const notices: UnpersistedUploadNotice[] = [];
	try {
		for (const key of Object.keys(localStorage).filter((key) => key.startsWith(PREFIX))) {
			try {
				const notice = JSON.parse(localStorage.getItem(key) ?? "null") as UnpersistedUploadNotice;
				if (
					!notice ||
					typeof notice.conversationId !== "string" ||
					typeof notice.organizationId !== "string" ||
					typeof notice.uploadBatchId !== "string" ||
					key !== PREFIX + notice.uploadBatchId ||
					typeof notice.laneId !== "string" ||
					!Number.isFinite(notice.createdAt) ||
					!Array.isArray(notice.files) ||
					notice.files.some(
						(file) =>
							!file ||
							!Number.isInteger(file.index) ||
							file.index < 0 ||
							typeof file.name !== "string" ||
							!Number.isFinite(file.size) ||
							file.size < 0 ||
							typeof file.type !== "string" ||
							(file.sessionId !== undefined && typeof file.sessionId !== "string") ||
							(file.persisted !== undefined && typeof file.persisted !== "boolean") ||
							(file.failureMessage !== undefined && typeof file.failureMessage !== "string") ||
							!validTarget(file.target)
					)
				) {
					console.warn("[Upload] Invalid unpersisted batch notice", { key });
					localStorage.removeItem(key);
					continue;
				}
				if (Date.now() - notice.createdAt >= TTL) {
					console.warn("[Upload] Unpersisted batch notice expired", {
						conversationId: notice.conversationId,
						uploadBatchId: notice.uploadBatchId
					});
					localStorage.removeItem(key);
					continue;
				}
				notices.push(notice);
			} catch (error) {
				console.warn("[Upload] Could not read unpersisted batch notice", { key, error });
			}
		}
	} catch (error) {
		console.warn("[Upload] Unpersisted batch notice storage unavailable", { error });
	}
	return notices;
}
export function rememberUnpersistedUploadNotice(notice: UnpersistedUploadNotice): boolean {
	const notices = readUploadBatchRecords().sort((left, right) => left.createdAt - right.createdAt);
	const removeCount = Math.max(0, notices.length - MAX_BATCHES + 1);
	const durable = notices.filter(batch => batch.files.every(file => file.persisted && !file.failureMessage));
	if (durable.length < removeCount) {
		console.warn("[Upload] Upload batch admission refused; unread recovery records fill the notice limit", {
			conversationId: notice.conversationId, uploadBatchId: notice.uploadBatchId
		});
		return false;
	}
	for (const oldest of durable.slice(0, removeCount)) {
		console.warn("[Upload] Unpersisted batch notice limit reached", {
			conversationId: oldest.conversationId,
			uploadBatchId: oldest.uploadBatchId
		});
		write({ ...oldest, files: [] });
	}
	return write({ ...notice, files: notice.files.map((file) => ({ ...file, target: file.target && abortIdentity(file.target) })) });
}
export function readUnpersistedUploadNotices(): UnpersistedUploadNotice[] {
	return readUploadBatchRecords()
		.map((notice) => ({ ...notice, files: notice.files.filter((file) => !file.persisted && !file.failureMessage) }))
		.filter((notice) => notice.files.length > 0);
}

export function updateUploadBatchFile(
	uploadBatchId: string,
	index: number,
	changes: Pick<Partial<UnpersistedUploadFile>, "sessionId" | "persisted" | "target" | "failureMessage">
): boolean {
	const notice = readUploadBatchRecords().find((notice) => notice.uploadBatchId === uploadBatchId);
	return notice
		? write({
				...notice,
				files: notice.files.map((file) =>
					file.index === index
						? { ...file, ...changes, ...(changes.target ? { target: abortIdentity(changes.target) } : {}) }
						: file
				)
			})
		: false;
}

export function forgetUploadBatchFile(uploadBatchId: string | undefined, index: number | undefined, onlyUnpersisted = false): void {
	if (uploadBatchId === undefined || index === undefined) return;
	const notice = readUploadBatchRecords().find((notice) => notice.uploadBatchId === uploadBatchId);
	if (notice)
		write({
			...notice,
			files: notice.files.filter((file) => file.index !== index || (onlyUnpersisted && (file.persisted || file.failureMessage)))
		});
}

export function forgetUnpersistedUploadFile(uploadBatchId: string | undefined, index: number | undefined): void {
	forgetUploadBatchFile(uploadBatchId, index, true);
}
